import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';

const teamMembers = [
  {
    name: '李明',
    role: '创始人 & CEO',
    bio: '前阿里巴巴高级产品经理，10年互联网产品经验。热爱钻研各种省钱技巧，希望通过福利吧帮助更多人享受品质生活。',
    image: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20portrait%20in%20modern%20office%2C%20warm%20minimalist%20beige%20background%2C%20friendly%20expression%2C%20clean%20corporate%20headshot%20style%2C%20soft%20studio%20lighting&width=400&height=400&seq=team-01&orientation=squarish',
  },
  {
    name: '王芳',
    role: '内容总监',
    bio: '曾任职于多家知名消费媒体，对优惠信息和消费趋势有敏锐洞察。负责福利吧全部内容的策划和审核。',
    image: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20portrait%20in%20modern%20office%2C%20warm%20minimalist%20beige%20background%2C%20confident%20expression%2C%20clean%20corporate%20headshot%20style%2C%20soft%20studio%20lighting&width=400&height=400&seq=team-02&orientation=squarish',
  },
  {
    name: '张伟',
    role: '技术负责人',
    bio: '全栈工程师，曾主导多个千万级用户产品的技术架构。负责福利吧平台的技术开发和数据安全。',
    image: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20portrait%20in%20casual%20tech%20office%2C%20warm%20minimalist%20beige%20background%2C%20thoughtful%20expression%2C%20clean%20headshot%20style%2C%20soft%20studio%20lighting&width=400&height=400&seq=team-03&orientation=squarish',
  },
];

const testimonials = [
  {
    name: '小陈',
    text: '福利吧真的是我的省钱神器！通过上面的优惠券，我每个月能省下好几百块。推荐给了身边所有朋友，大家都说好用！',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20person%20casual%20portrait%2C%20warm%20friendly%20expression%2C%20minimalist%20cream%20background%2C%20clean%20headshot%20style%2C%20soft%20lighting&width=200&height=200&seq=avatar-01&orientation=squarish',
  },
  {
    name: '大刘',
    text: '作为一个精打细算的上班族，福利吧帮我找到了很多隐藏的会员福利。特别是信用卡积分兑换的攻略，让我每年多赚了2000多块。',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20professional%20Chinese%20male%20portrait%2C%20friendly%20smile%2C%20minimalist%20cream%20background%2C%20clean%20headshot%20style%2C%20soft%20lighting&width=200&height=200&seq=avatar-02&orientation=squarish',
  },
  {
    name: '小雨',
    text: '我是学生党，预算有限。福利吧推荐的免费资源和折扣好物简直就是为我量身定做的。现在买东西之前都会先来福利吧看看。',
    avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20female%20student%20portrait%2C%20warm%20smile%2C%20minimalist%20cream%20background%2C%20clean%20headshot%20style%2C%20soft%20lighting&width=200&height=200&seq=avatar-03&orientation=squarish',
  },
  {
    name: '老周',
    text: '做电商运营的我，对优惠信息的真实性非常挑剔。福利吧是我见过最靠谱的福利平台，每条优惠都经过验证，几乎没有失效的情况。',
    avatar: 'https://readdy.ai/api/search-image?query=Middle%20aged%20Chinese%20male%20professional%20portrait%2C%20friendly%20expression%2C%20minimalist%20cream%20background%2C%20clean%20headshot%20style%2C%20soft%20lighting&width=200&height=200&seq=avatar-04&orientation=squarish',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qmp1ee3oq4jqumtn5g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const code = parsed?.code as string | undefined;
      const serverMsg = (parsed?.meta as Record<string, string> | undefined)?.message || (parsed?.message as string) || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  }, []);

  return (
    <Layout>
      <SEOHead
        title="关于我们 - 福利吧"
        description="福利吧品牌介绍——专注精选优惠、折扣、免费资源与会员权益的福利聚合平台。了解我们的团队、用户口碑和联系方式。"
        keywords="关于福利吧,福利聚合平台,品牌介绍,联系方式"
        canonicalUrl="https://fuliba.com/about"
      />
      <SchemaOrg type="Organization" data={{
        name: '福利吧',
        url: 'https://fuliba.com',
        description: '福利吧是专注精选优惠、折扣、免费资源与会员权益的福利聚合平台。',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          email: 'contact@fuliba.com',
        },
      }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://fuliba.com' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      }} />

      <div className="pt-16">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        <section className="w-full bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-14">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                关于福利吧
              </h1>
              <p className="mt-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                福利吧成立于2024年，是国内领先的福利聚合平台。我们专注于精选优惠、折扣、免费资源与会员权益，每日更新海量好物福利，帮助用户以最低成本享受最高品质生活。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
              <div>
                <h2 className="text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  我们的使命
                </h2>
                <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                  在信息爆炸的时代，消费者面对海量优惠信息往往无所适从——有些优惠已经过期，有些存在隐藏条件，有些甚至是虚假宣传。福利吧的使命就是帮助用户过滤噪音，精选真正有价值的福利信息。
                </p>
                <p className="text-foreground-700 text-sm leading-relaxed">
                  我们的团队每天从数百个渠道筛选、验证优惠信息，确保每一条推送都是真实、有效、有价值的。我们相信，省钱不应该是一件费时费力的事——把筛选的工作交给我们，把省下的时间和金钱留给生活。
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                  核心优势
                </h2>
                <div className="space-y-3">
                  {[
                    { icon: 'ri-shield-check-line', title: '真实可靠', desc: '每条优惠信息都经过人工验证，确保真实有效' },
                    { icon: 'ri-refresh-line', title: '每日更新', desc: '每天更新3000+条福利内容，不漏掉任何好价' },
                    { icon: 'ri-group-line', title: '用户至上', desc: '50万+活跃用户的选择，口碑见证品质' },
                    { icon: 'ri-lightbulb-line', title: '深度攻略', desc: '不仅告诉你有什么福利，更教你如何最大化利用' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-background-100">
                      <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-primary-600 text-sm`}></i>
                      </div>
                      <div>
                        <strong className="text-sm text-foreground-900 block">{item.title}</strong>
                        <span className="text-xs text-foreground-600">{item.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-14">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                我们的团队
              </h2>
              <p className="mt-3 text-foreground-600 text-sm">一群热爱生活、追求品质的省钱达人</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {teamMembers.map((member, index) => (
                <div key={index} className="text-center p-5 rounded-xl bg-background-50">
                  <div className="w-24 h-24 mx-auto rounded-full overflow-hidden mb-4">
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                  </div>
                  <h3 className="font-semibold text-foreground-950 text-base">{member.name}</h3>
                  <p className="text-xs text-primary-600 font-medium mt-0.5">{member.role}</p>
                  <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-14">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                用户口碑
              </h2>
              <p className="mt-3 text-foreground-600 text-sm">听听他们怎么说</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {testimonials.map((t, index) => (
                <div key={index} className="p-5 rounded-xl bg-background-100 flex gap-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-2">&ldquo;{t.text}&rdquo;</p>
                    <span className="text-xs font-semibold text-foreground-900">{t.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100/70">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-14">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                联系我们
              </h2>
              <p className="mt-3 text-foreground-600 text-sm">有任何问题或合作意向，欢迎与我们联系</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', content: 'contact@fuliba.com' },
                { icon: 'ri-wechat-line', title: '微信公众号', content: '福利吧FuliBar' },
                { icon: 'ri-map-pin-line', title: '公司地址', content: '中国上海市浦东新区' },
              ].map((item, i) => (
                <div key={i} className="text-center p-4 rounded-xl bg-background-50">
                  <div className="w-10 h-10 mx-auto rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                    <i className={`${item.icon} text-primary-600`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 text-sm">{item.title}</h3>
                  <p className="text-xs text-foreground-600 mt-1">{item.content}</p>
                </div>
              ))}
            </div>

            <form
              className="bg-background-50 rounded-2xl p-6 md:p-8"
              data-readdy-form
              onSubmit={handleSubmit}
            >
              <h3 className="text-lg font-semibold text-foreground-950 mb-6">发送消息</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                  placeholder="请输入主题"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                  placeholder="请输入您的留言内容（500字以内）"
                ></textarea>
              </div>

              <div className="anti-spam-field">
                <label htmlFor="phone_alt">备用电话</label>
                <input type="text" id="phone_alt" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>

              {formStatus === 'success' && (
                <div className="mb-4 p-3 rounded-lg bg-accent-100/80 text-accent-800 text-sm flex items-center gap-2">
                  <i className="ri-checkbox-circle-line"></i>
                  消息已发送成功，我们会尽快回复您！
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-4 p-3 rounded-lg bg-primary-100/80 text-primary-800 text-sm flex items-center gap-2">
                  <i className="ri-error-warning-line"></i>
                  {formError || '提交失败，请稍后重试'}
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                发送消息
              </button>
            </form>
          </div>
        </section>
      </div>
    </Layout>
  );
}